import React, { forwardRef, useContext } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { rgba, darken } from "polished";

import { Chip, Collapse, ListItemButton, ListItemText, Tooltip } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import { SidebarCollapsedContext } from "./Sidebar";
import { fontFamily, fontSizes, fontWeight, letterSpacings, lineHeights, zIndex } from "../../design-system/tokens";

const CustomRouterLink = forwardRef((props, ref) => (
  <div ref={ref}>
    <NavLink {...props} />
  </div>
));

CustomRouterLink.displayName = "CustomRouterLink";

const Item = styled(ListItemButton)`
  padding: ${(props) =>
    props.theme.spacing(1.5, 2)}; /* py:1.5 px:2 = 12px 16px with spacing=8 */
  min-height: 44px;
  gap: ${(props) =>
    props.theme.spacing(2.5)}; /* gap:2.5 = 20px with spacing=8 */
  padding-left: ${(props) =>
    props.$collapsed
      ? 0
      : props.depth && props.depth > 0
      ? props.theme.spacing(7)
      : props.theme.spacing(2)}; /* LWI-720: flush 0 when collapsed; otherwise 56px nested or 16px */
  padding-right: ${(props) =>
    props.$collapsed ? 0 : props.theme.spacing(2)};
  justify-content: ${(props) => (props.$collapsed ? "center" : "flex-start")};
  font-family: ${fontFamily.sans};
  font-weight: 400;
  font-size: ${fontSizes.base};
  line-height: ${lineHeights.md};
  letter-spacing: ${letterSpacings.body1};
  /* LWI-1096 — colors come from the theme's sidebar tokens so every
     variant (light or dark sidebar) stays readable. On the Default/Dark
     navy these compute to the previously hardcoded values. */
  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: ${fontSizes["2xl"]};
    width: 20px;
    height: 20px;
    opacity: 0.5;
    min-width: auto;
  }
  &:hover {
    background: ${(props) => rgba(props.theme.sidebar.color, 0.08)};
    color: ${(props) => props.theme.sidebar.color};
  }
  &.${(props) => props.activeclassname} {
    background-color: ${(props) =>
      darken(0.05, props.theme.sidebar.background)};
    /* LWI-1592 — brand marker on the row's left edge, absolutely placed so
       padding and icon/label alignment don't move (expanded, nested or
       collapsed). The white/600 label is what carries the state; the red is
       only 2.2:1 on the navy. */
    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      border-radius: 0 2px 2px 0;
      background-color: ${(props) => props.theme.sidebar.activeMarker};
    }
    span {
      color: ${(props) => props.theme.sidebar.activeColor};
      font-weight: ${fontWeight.semibold};
    }
    &:hover {
      background-color: ${(props) =>
        darken(0.05, props.theme.sidebar.background)};
    }
  }
`;

const Title = styled(ListItemText)`
  margin: 0;
  span {
    color: ${(props) => props.theme.sidebar.color};
    font-family: ${fontFamily.sans};
    font-size: ${fontSizes.base};
    line-height: ${lineHeights.md};
    letter-spacing: ${letterSpacings.body1};
    padding: 0;
  }
`;

const Badge = styled(Chip)`
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 26px;
  top: 12px;
  background: ${(props) => props.theme.sidebar.badge.background};
  z-index: ${zIndex.raised};
  span.MuiChip-label,
  span.MuiChip-label:hover {
    font-size: ${fontSizes.xs};
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`;

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const SidebarNavListItem = (props) => {
  const {
    title,
    href,
    disabled,
    depth = 0,
    children,
    icon: Icon,
    badge,
    open: openProp = false,
  } = props;

  const [open, setOpen] = React.useState(openProp);
  // LWI-720 — when the desktop sidebar is collapsed, render icon-only
  // items with a hover tooltip showing the full title.
  const collapsed = useContext(SidebarCollapsedContext);

  const handleToggle = () => {
    setOpen((state) => !state);
  };

  // Tooltip shows the title only when the sidebar is collapsed; otherwise
  // its empty-string title prop suppresses the tooltip entirely.
  const renderWithTooltip = (item) =>
    collapsed ? (
      <Tooltip title={title} placement="right" arrow>
        {item}
      </Tooltip>
    ) : (
      item
    );

  if (children) {
    // LWI-720 — when collapsed, parent items don't expand inline (the
    // child rows would have nowhere to render in 64px). Treat a click as
    // a no-op visual; users who need the children expand the sidebar.
    return (
      <React.Fragment>
        {renderWithTooltip(
          <Item
            depth={depth}
            $collapsed={collapsed}
            onClick={collapsed ? undefined : handleToggle}
          >
            {Icon && <Icon />}
            {!collapsed && (
              <Title depth={depth}>
                {title}
                {badge && <Badge label={badge} />}
              </Title>
            )}
            {!collapsed && (open ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          </Item>
        )}
        {!collapsed && <Collapse in={open}>{children}</Collapse>}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {renderWithTooltip(
        <Item
          depth={depth}
          $collapsed={collapsed}
          disabled={disabled}
          component={CustomRouterLink}
          to={href}
          activeclassname="active"
        >
          {Icon && <Icon />}
          {!collapsed && (
            <Title depth={depth}>
              {title}
              {badge && <Badge label={badge} />}
            </Title>
          )}
        </Item>
      )}
    </React.Fragment>
  );
};

export default SidebarNavListItem;
